import type { RoleType, VendorDTO } from "@mydailydirect/api-client";

/**
 * The signed-in session.
 */
export interface Session {
  tenantId: string;
  userId: string;
  fullName: string;
  roles: RoleType[];
  phone?: string;
  phoneNumber?: string;
  orgId?: string;
  organizationId?: string;
  businessName?: string;
  accountType?: string;
  email?: string;
  createdAt?: string;
}

export interface UserRecord {
  userId: string;
  passwordHash: string; // Plaintext or simple hash for local dev
  fullName: string;
  roles: RoleType[];
  phone?: string;
  phoneNumber?: string;
  orgId?: string;
  organizationId?: string;
  businessName?: string;
  accountType?: string;
  city?: string;
  gstNumber?: string;
  createdAt: string;
}

const SESSION_STORAGE_KEY = "mydailydirect.session.v3";
const USERS_STORAGE_KEY = "mydailydirect.users.v4";
const LEGACY_STORAGE_KEYS = [
  "mydailydirect.session",
  "mydailydirect.session.v2",
  "mydailydirect.users",
  "mydailydirect.users.v2",
  "mydailydirect.users.v3",
  "mydailydirect.vendors",
  "mydailydirect.vendors.v1",
  "mydailydirect.vendors.v2",
  "trado.session",
  "trado.users",
];

function purgeLegacyStorage(): void {
  try {
    for (const key of LEGACY_STORAGE_KEYS) {
      localStorage.removeItem(key);
    }
  } catch {
    // Ignore storage errors
  }
}

export const SUPER_ADMIN_PHONES = ["9962233577", "7075678910"] as const;

export const ALL_ROLES: RoleType[] = ["ADMIN", "RETAILER", "TRADER", "RM", "DRIVER", "VENDOR"];
export const SUPER_ADMIN_ROLES: RoleType[] = ["SUPER_ADMIN", ...ALL_ROLES];

/**
 * Returns true if the given phone number belongs to one of the two Co-Founders
 * (+919962233577 or +917075678910).
 */
export function isSuperAdminPhone(value: string | null | undefined): boolean {
  const digits = (value ?? "").replace(/\D/g, "");
  const norm = digits.length >= 10 ? digits.slice(-10) : "";
  return (SUPER_ADMIN_PHONES as readonly string[]).includes(norm);
}

/**
 * Returns true if and only if the current session is authenticated as one of the two
 * Co-Founder Super Admins (+919962233577 or +917075678910).
 */
export function isSuperAdminSession(session: Session | null | undefined): boolean {
  if (!session || !session.userId || session.userId === "system.bootstrap") {
    return false;
  }
  const hasSuperOrAdminRole =
    session.roles?.includes("SUPER_ADMIN") || session.roles?.includes("ADMIN");
  if (!hasSuperOrAdminRole) {
    return false;
  }
  return (
    isSuperAdminPhone(session.phone) ||
    isSuperAdminPhone(session.phoneNumber) ||
    isSuperAdminPhone(session.userId)
  );
}

// Production Pre-seeded Admin & Co-Founder Accounts
export const PRODUCTION_USERS: UserRecord[] = [
  {
    userId: "admin",
    passwordHash: "admin123",
    fullName: "Co-Founder Super Admin",
    roles: [...SUPER_ADMIN_ROLES],
    phone: "7075678910",
    phoneNumber: "7075678910",
    orgId: "org_mdd_hq",
    organizationId: "org_mdd_hq",
    businessName: "My Daily Direct HQ",
    accountType: "Super Admin",
    city: "Bengaluru",
    createdAt: new Date().toISOString(),
  },
  {
    userId: "support.admin",
    passwordHash: "admin123",
    fullName: "Support Desk Administrator",
    roles: [...ALL_ROLES],
    phone: "9180348180",
    phoneNumber: "9180348180",
    orgId: "org_mdd_hq",
    organizationId: "org_mdd_hq",
    businessName: "My Daily Direct HQ Support",
    accountType: "Admin",
    city: "Bengaluru",
    createdAt: new Date().toISOString(),
  },
  {
    userId: "user_3577",
    passwordHash: "otp_verified",
    fullName: "Vignesh Nagarajan (Co-Founder)",
    roles: [...SUPER_ADMIN_ROLES],
    phone: "9962233577",
    phoneNumber: "9962233577",
    orgId: "org_mdd_admin",
    organizationId: "org_mdd_admin",
    businessName: "My Daily Direct HQ",
    accountType: "Super Admin",
    city: "Bengaluru",
    createdAt: new Date().toISOString(),
  },
  {
    userId: "dock.admin",
    passwordHash: "dock123",
    fullName: "Dock Operations Admin",
    roles: ["TRADER"],
    phone: "7075678910",
    phoneNumber: "7075678910",
    orgId: "org_mdd_wh",
    organizationId: "org_mdd_wh",
    businessName: "Central Warehouse Dock",
    accountType: "Dock Admin",
    city: "Bengaluru",
    createdAt: new Date().toISOString(),
  },
];

export function loadUsers(): UserRecord[] {
  try {
    purgeLegacyStorage();
    const raw = localStorage.getItem(USERS_STORAGE_KEY);
    if (!raw) {
      localStorage.setItem(USERS_STORAGE_KEY, JSON.stringify(PRODUCTION_USERS));
      return PRODUCTION_USERS;
    }
    const parsed = JSON.parse(raw) as UserRecord[];
    if (!Array.isArray(parsed) || parsed.length === 0) {
      return PRODUCTION_USERS;
    }
    // Ensure pre-seeded production users are always present with up-to-date roles and phone numbers
    const merged = [...PRODUCTION_USERS];
    for (const u of parsed) {
      const uPhone = normalizePhone(u.phone);
      const isPreseeded = merged.some(
        (p) =>
          p.userId.toLowerCase() === u.userId.toLowerCase() ||
          (uPhone.length > 0 &&
            normalizePhone(p.phone) === uPhone &&
            p.roles.includes("ADMIN")),
      );
      if (!isPreseeded) {
        merged.push(
          u.roles?.includes("ADMIN")
            ? { ...u, roles: [...ALL_ROLES] }
            : u,
        );
      }
    }
    return merged;
  } catch {
    return PRODUCTION_USERS;
  }
}

export function saveUsers(users: UserRecord[]): void {
  localStorage.setItem(USERS_STORAGE_KEY, JSON.stringify(users));
}

/**
 * Normalises an Indian mobile number to its 10 significant digits, so "+91 70756 78910",
 * "919180348180" and "9180348180" all compare equal. Returns "" when fewer than 10 digits.
 */
export function normalizePhone(value: string | null | undefined): string {
  const digits = (value ?? "").replace(/\D/g, "");
  return digits.length >= 10 ? digits.slice(-10) : "";
}

export function findUser(userIdOrPhone: string, requiredRoles?: RoleType[]): UserRecord | null {
  const users = loadUsers();
  const query = userIdOrPhone.trim().toLowerCase();
  const phone = normalizePhone(query);
  const matches = users.filter(
    (u) =>
      u.userId.toLowerCase() === query ||
      (phone.length > 0 && normalizePhone(u.phone) === phone),
  );
  if (matches.length === 0) return null;
  if (requiredRoles && requiredRoles.length > 0) {
    return matches.find((u) => u.roles.some((r) => requiredRoles.includes(r))) ?? null;
  }
  return matches[0];
}

export function registerUserRecord(newUserData: Omit<UserRecord, "createdAt">): UserRecord {
  const users = loadUsers();
  const rawPhone = newUserData.phone || newUserData.phoneNumber;
  const newDigits = normalizePhone(rawPhone);
  const rawOrgId =
    newUserData.orgId ||
    newUserData.organizationId ||
    (newUserData.roles.includes("ADMIN") ? "org_mdd_admin" : "mydailydirect");

  const effectiveRoles = newUserData.roles.includes("ADMIN")
    ? [...ALL_ROLES]
    : newUserData.roles;
  const existingIdx = users.findIndex(
    (u) =>
      u.userId.toLowerCase() === newUserData.userId.toLowerCase() ||
      (newDigits.length > 0 &&
        normalizePhone(u.phone || u.phoneNumber) === newDigits &&
        u.roles.some((r) => effectiveRoles.includes(r))),
  );

  const record: UserRecord = {
    ...newUserData,
    phone: rawPhone,
    phoneNumber: rawPhone,
    orgId: rawOrgId,
    organizationId: rawOrgId,
    roles: effectiveRoles,
    createdAt: existingIdx >= 0 ? users[existingIdx].createdAt : new Date().toISOString(),
  };

  const updated =
    existingIdx >= 0
      ? users.map((u, idx) => (idx === existingIdx ? record : u))
      : [...users, record];
  saveUsers(updated);
  return record;
}

export function loadSession(): Session | null {
  try {
    purgeLegacyStorage();
    const raw = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Session;
    if (!parsed.userId) return null;
    if (
      parsed.userId === "vendor.demo" ||
      parsed.userId === "vendor.balaji" ||
      parsed.phone === "9886012345" ||
      parsed.phone === "9845098450"
    ) {
      localStorage.removeItem(SESSION_STORAGE_KEY);
      return null;
    }
    parsed.tenantId = "mydailydirect";

    // Synchronize phone and phoneNumber
    const userDigits = normalizePhone(parsed.userId);
    const rawPhone =
      parsed.phone ||
      parsed.phoneNumber ||
      (userDigits.length >= 10 ? userDigits : undefined);
    if (rawPhone) {
      parsed.phone = rawPhone;
      parsed.phoneNumber = rawPhone;
    }

    // Synchronize orgId and organizationId
    const rawOrgId =
      parsed.orgId ||
      parsed.organizationId ||
      (parsed.roles?.includes("ADMIN") ? "org_mdd_admin" : (parsed.tenantId || "mydailydirect"));
    parsed.orgId = rawOrgId;
    parsed.organizationId = rawOrgId;

    const digits = normalizePhone(parsed.phone || parsed.userId);
    const isAdminUser =
      parsed.roles?.includes("ADMIN") ||
      parsed.roles?.includes("SUPER_ADMIN") ||
      PRODUCTION_USERS.some(
        (p) =>
          (p.userId.toLowerCase() === parsed.userId.toLowerCase() ||
            (digits.length > 0 && normalizePhone(p.phone) === digits)) &&
          p.roles.includes("ADMIN"),
      );
    if (isAdminUser) {
      parsed.roles = isSuperAdminPhone(digits) ? [...SUPER_ADMIN_ROLES] : [...ALL_ROLES];
    }
    return parsed;
  } catch {
    return null;
  }
}

export function saveSession(session: Session): void {
  purgeLegacyStorage();
  const userDigits = normalizePhone(session.userId);
  const rawPhone =
    session.phone ||
    session.phoneNumber ||
    (userDigits.length >= 10 ? userDigits : undefined);
  const isAdminOrSuper =
    session.roles?.includes("ADMIN") || session.roles?.includes("SUPER_ADMIN");
  const roles = isAdminOrSuper
    ? isSuperAdminPhone(rawPhone || session.userId)
      ? [...SUPER_ADMIN_ROLES]
      : [...ALL_ROLES]
    : session.roles;
  const rawOrgId =
    session.orgId ||
    session.organizationId ||
    (session.roles?.includes("ADMIN") ? "org_mdd_admin" : (session.tenantId || "mydailydirect"));

  const cleanSession: Session = {
    ...session,
    tenantId: "mydailydirect",
    roles,
    phone: rawPhone,
    phoneNumber: rawPhone,
    orgId: rawOrgId,
    organizationId: rawOrgId,
  };
  localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(cleanSession));
}

export function clearSession(): void {
  purgeLegacyStorage();
  localStorage.removeItem(SESSION_STORAGE_KEY);
}

export const VENDORS_STORAGE_KEY = "mydailydirect.vendors.v3";

export const DEFAULT_VENDORS: VendorDTO[] = [];

export function loadVendors(): VendorDTO[] {
  try {
    purgeLegacyStorage();
    const raw = typeof window !== "undefined" ? localStorage.getItem(VENDORS_STORAGE_KEY) : null;
    let list: VendorDTO[] = [];
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          list = parsed.filter(
            (v: VendorDTO) =>
              v.phone !== "9886012345" &&
              v.phone !== "9845098450" &&
              !v.legalName?.toLowerCase().includes("sri lakshmi agro mills"),
          );
        }
      } catch {
        // invalid JSON
      }
    }

    // Auto-detect any users registered as VENDOR and ensure they are listed in vendors
    const users = loadUsers();
    let updated = false;
    for (const u of users) {
      if (u.roles?.includes("VENDOR" as any) || u.accountType?.toUpperCase() === "VENDOR") {
        const uPhone = normalizePhone(u.phone);
        const exists = list.some(
          (v) =>
            (uPhone.length > 0 && normalizePhone(v.phone) === uPhone) ||
            v.legalName.toLowerCase() === (u.businessName || u.fullName).toLowerCase(),
        );
        if (!exists) {
          list.push({
            id: Date.now() + Math.floor(Math.random() * 1000),
            legalName: u.businessName || u.fullName || "Vendor Partner",
            tradeName: u.businessName || u.fullName,
            contactName: u.fullName,
            phone: u.phone || u.userId,
            city: u.city || "Bengaluru",
            gstNumber: u.gstNumber,
            status: "APPROVED",
            commissionPercent: 0,
            payoutCycle: "Weekly T+7",
            createdAt: u.createdAt || new Date().toISOString(),
          });
          updated = true;
        }
      }
    }

    if (updated && typeof window !== "undefined") {
      localStorage.setItem(VENDORS_STORAGE_KEY, JSON.stringify(list));
    }
    return list;
  } catch {
    return DEFAULT_VENDORS;
  }
}

export function saveVendors(vendors: VendorDTO[]): void {
  try {
    if (typeof window !== "undefined") {
      localStorage.setItem(VENDORS_STORAGE_KEY, JSON.stringify(vendors));
    }
  } catch {
    // Ignore storage quota errors
  }
}

export function registerVendorRecord(vendor: VendorDTO): VendorDTO {
  const current = loadVendors();
  const phone = normalizePhone(vendor.phone);
  const existingIdx = current.findIndex(
    (v) => (phone.length > 0 && normalizePhone(v.phone) === phone) || v.id === vendor.id,
  );

  const updatedVendor: VendorDTO = {
    ...vendor,
    id: vendor.id || (existingIdx >= 0 ? current[existingIdx].id : Date.now()),
    createdAt: existingIdx >= 0 ? current[existingIdx].createdAt : (vendor.createdAt || new Date().toISOString()),
  };

  const nextList =
    existingIdx >= 0
      ? current.map((v, i) => (i === existingIdx ? updatedVendor : v))
      : [...current, updatedVendor];

  saveVendors(nextList);
  return updatedVendor;
}

export function findLocalVendorByPhone(phone: string): VendorDTO | null {
  const list = loadVendors();
  const clean = normalizePhone(phone);
  if (!clean) return null;
  return (
    list.find((v) => {
      if (normalizePhone(v.phone) === clean) return true;
      if (normalizePhone(v.alternatePhone) === clean) return true;
      if (v.authorizedPhones) {
        const staff = v.authorizedPhones.split(/[,;\s]+/).map(normalizePhone);
        if (staff.includes(clean)) return true;
      }
      return false;
    }) ?? null
  );
}

export function updateLocalVendorStatus(
  id: number,
  status: string,
  reviewerNote?: string,
  approvedBy?: string,
): void {
  const current = loadVendors();
  const nextList = current.map((v) => {
    if (v.id === id) {
      return {
        ...v,
        status,
        reviewerNote: reviewerNote !== undefined ? reviewerNote : v.reviewerNote,
        approvedBy: status === "APPROVED" ? (approvedBy || "Admin") : v.approvedBy,
        approvedAt: status === "APPROVED" ? new Date().toISOString() : v.approvedAt,
      };
    }
    return v;
  });
  saveVendors(nextList);
}

